import { offlineCredentialsDB } from '../db/indexedDB';
import { hashPassword } from '../utils/passwordHash';
import { encryptText, decryptText } from '../utils/encryption';
import { ACCESS_TOKEN_TTL_MS } from '../config/api';

/**
 * Persist credentials required for offline login.
 */
export const saveOfflineCredential = async (email, password, authData) => {
  if (!email || !password || !authData) return;
  
  const passwordHash = await hashPassword(email, password);
  let encryptedRefreshToken = null;
  
  if (authData.refresh_token) {
    try {
      encryptedRefreshToken = await encryptText(passwordHash, authData.refresh_token);
    } catch (error) {
      console.error('Failed to encrypt refresh token for offline storage:', error);
    }
  }
  
  const accessExpiresAt = authData.access_expires_at || authData.expires_at || new Date(Date.now() + ACCESS_TOKEN_TTL_MS).toISOString();
  
  await offlineCredentialsDB.save({
    email,
    password_hash: passwordHash,
    access_token: authData.access_token,
    user_info: authData.user_info,
    refresh_token_encrypted: encryptedRefreshToken?.cipher || null,
    refresh_token_iv: encryptedRefreshToken?.iv || null,
    refresh_token_alg: encryptedRefreshToken?.algorithm || null,
    access_expires_at: accessExpiresAt,
  });
};

/**
 * Validate the supplied credentials against the offline cache.
 * Returns the cached auth bundle when successful.
 */
export const getOfflineAuthForCredentials = async (email, password) => {
  if (!email || !password) return null;
  
  const credential = await offlineCredentialsDB.getByEmail(email);
  if (!credential) return null;
  
  const passwordHash = await hashPassword(email, password);
  if (credential.password_hash !== passwordHash) {
    return null;
  }

  let refreshToken = null;
  if (credential.refresh_token_encrypted) {
    try {
      refreshToken = await decryptText(passwordHash, {
        cipher: credential.refresh_token_encrypted,
        iv: credential.refresh_token_iv,
        algorithm: credential.refresh_token_alg,
      });
    } catch (error) {
      console.error('Failed to decrypt offline refresh token:', error);
      refreshToken = null;
    }
  }
  
  return {
    ...credential,
    refresh_token: refreshToken,
  };
};

export const clearOfflineCredentials = async () => {
  await offlineCredentialsDB.clear();
};


